import {AgentIdempotencyKey,AgentMutationResolution,ErrorEnvelope,agentOperationRoles,type AgentOperation} from '@zzy/contracts';

type Decoder<T>={parse(value:unknown):T};
export interface AgentIdentity {accountId:string;siteId:string;source:string;realm:'site'|'hq'}
export interface AgentSession {authenticated:boolean;csrf:string;accountId?:string;siteId:string;source?:string;realm:'site'|'hq';roles:string[];sessionEpoch?:string}
export interface PendingAgentMutation {operation:AgentOperation;key:string}
type StoragePort=Pick<Storage,'getItem'|'setItem'|'removeItem'>;
export class AgentClientError extends Error {
  constructor(message:string,readonly outcomeUnknown=false){super(message);this.name='AgentClientError'}
}
export class AgentIdentityError extends AgentClientError {
  constructor(message='账户或权限已变化，请刷新页面后重新登录。'){super(message);this.name='AgentIdentityError'}
}
function identityRejected(status:number,code:string){return status===401||status===403||code==='AGENT_SESSION_CHANGED'}
const fallback='暂时无法读取代理服务，请重新查询状态。';
export async function agentRead<T>(path:string,schema:Decoder<T>,fetcher:typeof fetch=fetch):Promise<T> {
  const response=await fetcher(path,{credentials:'same-origin',cache:'no-store',redirect:'error'});
  return decodeAgentResponse(response,schema);
}
async function decodeAgentResponse<T>(response:Response,schema:Decoder<T>):Promise<T>{
  const body:unknown=await response.json();
  if(!response.ok){const error=ErrorEnvelope.safeParse(body),message=error.success?error.data.error.message:fallback;
    throw identityRejected(response.status,error.success?error.data.error.code:'')?new AgentIdentityError(message):new AgentClientError(message)}
  if(!body||typeof body!=='object'||!('data'in body))throw new AgentClientError(fallback);
  try{return schema.parse(body.data)}catch{throw new AgentClientError(fallback)}
}
export async function agentSession(site?:string,fetcher:typeof fetch=fetch):Promise<AgentSession> {
  return agentRead('/api/v1/session'+(site?'?site='+encodeURIComponent(site):''),{parse(value){
    const data=value as Partial<AgentSession>|null;
    if(!data||typeof data.authenticated!=='boolean'||typeof data.csrf!=='string'||!data.csrf||typeof data.siteId!=='string'||
      !['site','hq'].includes(data.realm??'')||data.authenticated&&(typeof data.accountId!=='string'||typeof data.source!=='string'||typeof data.sessionEpoch!=='string'||!/^[a-f0-9]{64}$/.test(data.sessionEpoch))||
      data.roles!==undefined&&(!Array.isArray(data.roles)||!data.roles.every(role=>typeof role==='string')))throw Error('INVALID_SESSION');
    return {...data,roles:data.roles??[]} as AgentSession;
  }},fetcher);
}
export function requireAgentIdentity(session:AgentSession,identity:AgentIdentity){
  if(!session.authenticated||session.accountId!==identity.accountId||session.siteId!==identity.siteId||session.source!==identity.source||session.realm!==identity.realm)
    throw new AgentIdentityError('账户或站点已变化，请刷新页面后重新登录。');
}
export function requireAgentRoles(session:AgentSession,roles:readonly string[]){
  if(!roles.some(role=>session.roles.includes(role)))throw new AgentIdentityError('当前权限已变化，请刷新页面后重新登录。');
}
/** Only operation/key are retained. Contact information, attachments and CSRF are never stored. */
export class AgentLiveClient {
  readonly storageKey:string;
  constructor(readonly identity:AgentIdentity,readonly storage:StoragePort,readonly fetcher:typeof fetch=fetch){
    this.storageKey='zzy:agent:pending:'+JSON.stringify([identity.source,identity.siteId,identity.accountId,identity.realm]);
  }
  url(path:string){
    if(!path.startsWith(this.identity.realm==='hq'?'/api/v1/hq/agent-':'/api/v1/agent/'))throw new AgentClientError('无法访问此入口。');
    return this.identity.realm==='hq'?path+(path.includes('?')?'&':'?')+'site='+encodeURIComponent(this.identity.siteId):path;
  }
  pending():PendingAgentMutation|undefined {
    try{
      const raw=this.storage.getItem(this.storageKey);if(!raw)return;
      const data=JSON.parse(raw) as PendingAgentMutation;
      if(!data||!Object.hasOwn(agentOperationRoles,data.operation)||!AgentIdempotencyKey.safeParse(data.key).success)throw Error('INVALID_PENDING');
      return {operation:data.operation,key:data.key};
    }catch{throw new AgentClientError('未能读取待核对记录，请联系管理员处理。',true)}
  }
  async read<T>(path:string,schema:Decoder<T>){return agentRead(this.url(path),schema,this.fetcher)}
  private async session(operation?:AgentOperation){
    const current=await agentSession(this.identity.realm==='hq'?this.identity.siteId:undefined,this.fetcher);
    requireAgentIdentity(current,this.identity);
    if(operation)requireAgentRoles(current,agentOperationRoles[operation]);return current;
  }
  async write<T>(path:string,operation:AgentOperation,input:unknown,schema:Decoder<T>):Promise<T> {
    if(this.pending())throw new AgentClientError('请先查询上一笔操作的状态。',true);
    const session=await this.session(operation),key=crypto.randomUUID();
    try{this.storage.setItem(this.storageKey,JSON.stringify({operation,key}))}
    catch{throw new AgentClientError('无法保存操作编号，请允许本站使用会话存储后再试。')}
    let response:Response,body:unknown;
    try{
      const fetcher=this.fetcher;
      response=await fetcher(this.url(path),{method:'POST',credentials:'same-origin',cache:'no-store',redirect:'error',
        headers:{'Content-Type':'application/json','X-CSRF-Token':session.csrf,'X-Agent-Session-Epoch':session.sessionEpoch!,'Idempotency-Key':key},body:JSON.stringify(input)});
      body=await response.json();
    }catch{throw new AgentClientError('操作结果尚待核对，请重新查询状态，暂勿重复提交。',true)}
    if(!response.ok){
      const parsed=ErrorEnvelope.safeParse(body),code=parsed.success?parsed.data.error.code:'';
      const unknown=response.status>=500&&!['AGENT_SERVICE_NOT_READY','AGENT_ATTACHMENTS_UNAVAILABLE'].includes(code)||code==='AGENT_IDEMPOTENCY_CONFLICT';
      if(!unknown)this.storage.removeItem(this.storageKey);
      if(identityRejected(response.status,code))throw new AgentIdentityError(parsed.success?parsed.data.error.message:undefined);
      throw new AgentClientError(parsed.success?parsed.data.error.message:'操作结果尚待核对，请重新查询状态。',unknown);
    }
    if(!body||typeof body!=='object'||!('data'in body))throw new AgentClientError('操作结果尚待核对，请重新查询状态。',true);
    let value:T;try{value=schema.parse(body.data)}catch{throw new AgentClientError('操作结果尚待核对，请重新查询状态。',true)}
    this.storage.removeItem(this.storageKey);return value;
  }
  async resolve(){
    const pending=this.pending();if(!pending)return;
    const session=await this.session(pending.operation);
    const path=this.identity.realm==='hq'?'/api/v1/hq/agent-mutations/':'/api/v1/agent/mutations/';
    const fetcher=this.fetcher;
    const response=await fetcher(this.url(path+encodeURIComponent(pending.key)),{method:'POST',credentials:'same-origin',cache:'no-store',redirect:'error',
      headers:{'Content-Type':'application/json','X-CSRF-Token':session.csrf,'X-Agent-Session-Epoch':session.sessionEpoch!,'Idempotency-Key':pending.key},body:JSON.stringify({operation:pending.operation})});
    const result=await decodeAgentResponse(response,AgentMutationResolution);
    if(result.state!=='pending')this.storage.removeItem(this.storageKey);
    return result;
  }
}
